
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface University {
  id: string;
  name_en: string;
  name_ar: string;
  city: string;
}

interface UniversitySearchProps {
  currentLang: string;
  onUniversitySelect?: (universityId: string) => void;
  selectedUniversity?: string;
}

const UniversitySearch = ({ currentLang, onUniversitySelect, selectedUniversity }: UniversitySearchProps) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [universities, setUniversities] = useState<University[]>([]);
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      console.log('Fetching universities for UniversitySearch...');
      const { data, error } = await supabase
        .from('universities')
        .select('id, name_en, name_ar, city')
        .order('name_en');

      if (error) {
        console.error('Error fetching universities:', error);
        toast.error('Failed to load universities');
        return;
      }

      console.log('Universities fetched for search:', data);
      
      if (!data || data.length === 0) {
        console.warn('No universities found in database for search');
        setUniversities([]);
      } else {
        setUniversities(data);
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
      toast.error('Failed to load universities');
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() && universities.length > 0) {
      const filtered = universities.filter(university => {
        const nameToSearch = currentLang === 'ar' ? university.name_ar : university.name_en;
        const cityToSearch = university.city || '';
        
        return nameToSearch.toLowerCase().includes(searchTerm.toLowerCase()) ||
               cityToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredUniversities(filtered);
      setShowDropdown(true);
    } else {
      setFilteredUniversities([]);
      setShowDropdown(false);
    }
  }, [searchTerm, universities, currentLang]);

  useEffect(() => {
    if (selectedUniversity && universities.length > 0) {
      const university = universities.find(u => u.id === selectedUniversity);
      if (university) {
        setSearchTerm(currentLang === 'ar' ? university.name_ar : university.name_en);
      }
    }
  }, [selectedUniversity, currentLang, universities]);

  const handleUniversitySelect = (university: University) => {
    const displayName = currentLang === 'ar' ? university.name_ar : university.name_en;
    setSearchTerm(displayName);
    setShowDropdown(false);
    onUniversitySelect?.(university.id);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!e.target.value.trim()) {
      setShowDropdown(false);
    }
  };

  const handleInputFocus = () => {
    if (searchTerm.trim() && filteredUniversities.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  if (loading) {
    return (
      <div className="relative w-full max-w-2xl mx-auto">
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder={t('loading_universities', 'جاري تحميل الجامعات...', 'Loading universities...')}
            disabled
            className="pl-10 pr-4"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          type="text"
          placeholder={t('search_university', 'ابحث عن جامعتك...', 'Search for your university...')}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="pl-10 pr-4"
        />
      </div>
      
      {showDropdown && filteredUniversities.length > 0 && (
        <Card className="absolute top-full left-0 right-0 mt-1 z-50 max-h-60 overflow-y-auto bg-background border shadow-lg">
          {filteredUniversities.map((university) => (
            <div
              key={university.id}
              className="p-3 hover:bg-accent cursor-pointer border-b last:border-b-0"
              onClick={() => handleUniversitySelect(university)}
            >
              <div className="font-medium">
                {currentLang === 'ar' ? university.name_ar : university.name_en}
              </div>
              <div className="text-sm text-muted-foreground">{university.city}</div>
            </div>
          ))}
        </Card>
      )}

      {showDropdown && filteredUniversities.length === 0 && searchTerm.trim() && (
        <Card className="absolute top-full left-0 right-0 mt-1 z-50 p-4 bg-background border shadow-lg">
          <div className="text-center text-muted-foreground">
            {t('no_universities_found', 'لا توجد جامعات', 'No universities found')}
          </div>
        </Card>
      )}

      {universities.length === 0 && !loading && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 p-4 bg-background border shadow-lg rounded-md">
          <div className="text-center text-muted-foreground text-sm">
            {t('no_universities_available', 'لا توجد جامعات متاحة في النظام', 'No universities available in the system')}
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversitySearch;
